import { z } from 'zod';

import { assert } from '@libraries/typescript/utilities';

const runtimeUrlSchema = z.union([
    z.url(),
    z.string().regex(/^\/.+/, 'Expected an absolute URL or root-relative path'),
]);

/**
 * Non-sensitive environment-specific config that is served with the web app.
 *
 * The values are defined in `apps/web/config/<environment>.json`.
 *
 * Values that are not required in every environment should be nullable, not
 * optional.
 */
export const runtimeConfigSchema = z
    .object({
        runEnvironment: z.enum(['development', 'staging', 'production']),
        sha: z.string(),
        branch: z.string().nullable(),
        bundleHash: z.string(),
        apiUrl: z.url(),
        kratosPublicUrl: runtimeUrlSchema,
        agentationEndpoint: z.url().nullable(),
        simulatorWorldUrl: runtimeUrlSchema.nullable(),

        sentryDsn: z.url().nullable(),
        datadogClientToken: z.string().nullable(),
        datadogRumApplicationId: z.string().nullable(),
        datadogRumClientToken: z.string().nullable(),
        datadogSite: z.string().nullable(),
        intercomAppId: z.string().nullable(),
        posthogKey: z.string().nullable(),
    })
    .strict();

type RuntimeConfig = z.infer<typeof runtimeConfigSchema>;
type RunEnvironment = RuntimeConfig['runEnvironment'];

let runtimeConfig: RuntimeConfig | null = null;
let promise: Promise<RuntimeConfig> | null = null;

export function initializeRuntimeConfig(): Promise<RuntimeConfig> {
    if (!promise) {
        promise = fetch('/config.json', { cache: 'no-store' })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`/config.json ${response.status}`);
                }
                return response.json();
            })
            .then((json) => {
                runtimeConfig = runtimeConfigSchema.parse(json);
                return runtimeConfig;
            });
    }
    return promise;
}

export function getRuntimeConfig(): RuntimeConfig {
    assert.isNotNull(runtimeConfig, 'Runtime config has not been initialized');
    return runtimeConfig;
}

export function getRunEnvironment(): RunEnvironment {
    assert.isNotNull(runtimeConfig, 'Runtime config has not been initialized');
    return runtimeConfig.runEnvironment;
}
